/** Tab 3: Employment */
import { useState } from 'react'
import {
  Alert, Button, Card, Form, Input, message, Space, DatePicker, Select, Switch, InputNumber, Row, Col,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { joiningApi, type JoiningDetail } from '@/features/hr/joiningApi'
import { designationsApi, reportingCandidatesApi } from '@/features/hr/enablementApi'
import { EMPLOYEE_CATEGORIES } from '@/shared/types/hr'

interface Props { detail: JoiningDetail }

const EMPLOYEE_TYPES = [
  'PRODUCTION_WORKER', 'MAINTENANCE_TECHNICIAN', 'QC_LAB', 'STORE',
  'SECURITY', 'HOUSEKEEPING', 'STAFF_OFFICE', 'CONTRACT_WORKER',
  'TRAINEE_APPRENTICE',
]

export default function EmploymentTab({ detail }: Props) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [dirty, setDirty] = useState(false)
  const supervisorSet = Form.useWatch('reporting_supervisor_id', form)
    ?? detail.employee.reporting_supervisor_id

  // Same designation source as EmployeesPage — keeps both UIs in sync.
  // Free text still allowed via Select's free-input behaviour for legacy /
  // custom roles not in the master list.
  const { data: designations } = useQuery({
    queryKey: ['hr-designations'],
    queryFn: () => designationsApi.list(),
  })

  // Reporting-supervisor candidates per the org chart — people holding the
  // parent designation of this employee's designation.
  const { data: reportingData } = useQuery({
    queryKey: ['hr-reporting-candidates', detail.employee.plant_id, detail.employee.designation],
    queryFn: () => reportingCandidatesApi.get({
      plant_id: detail.employee.plant_id as string,
      designation_code: (detail.employee.designation as string | undefined) || undefined,
      exclude_employee_id: detail.employee.id as string,
    }),
  })
  const supervisorOptions = (reportingData?.candidates ?? [])
    .map((e) => ({ value: e.id, label: `${e.emp_code} · ${e.emp_name}${e.designation ? ` (${e.designation})` : ''}` }))

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) => joiningApi.update(detail.id, values),
    onSuccess: () => {
      message.success('Saved')
      setDirty(false)
      qc.invalidateQueries({ queryKey: ['joining-detail', detail.id] })
    },
  })

  const initial: Record<string, unknown> = { ...detail.employee }
  const dateFields = ['joining_date', 'probation_end_date', 'confirmation_due_date', 'naps_enrolment_date']
  dateFields.forEach((f) => {
    if (initial[f]) initial[f] = dayjs(initial[f] as string)
  })

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initial}
      onValuesChange={() => setDirty(true)}
      onFinish={(values) => {
        const out: Record<string, unknown> = {}
        Object.keys(values).forEach((k) => {
          const v = values[k]
          out[k] = dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : v
        })
        save.mutate(out)
      }}
    >
      {!supervisorSet && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Reporting Supervisor not set"
          description="Pick a reporting supervisor below and Save. The employee cannot be activated until this is filled."
        />
      )}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="designation"
              label="Designation"
              tooltip="Pick from standard list. Free text still allowed for legacy / custom roles."
            >
              <Select
                showSearch
                allowClear
                placeholder="Pick designation (or type to search)"
                options={(designations ?? []).map((d) => ({
                  label: `${d.designation_name} (${d.designation_code})`,
                  value: d.designation_code,
                }))}
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="category" label="Category">
              <Select
                options={EMPLOYEE_CATEGORIES.map((c) => ({ label: c, value: c }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="joining_employee_type" label="Employee Type">
              <Select options={EMPLOYEE_TYPES.map((v) => ({ value: v, label: v.replace(/_/g, ' ') }))} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="reporting_supervisor_id"
              label="Reporting Supervisor"
              tooltip="Candidates are the people at the parent designation in the org chart. Required before activation."
              extra={reportingData
                ? (reportingData.filtered
                    ? `Showing ${reportingData.matched_designation_name ?? reportingData.parent_designation_name ?? 'parent role'} — per org chart.`
                    : (reportingData.parent_designation_name
                        ? `No active ${reportingData.parent_designation_name} to report to yet — showing all active staff. Pick an interim supervisor.`
                        : 'No parent designation mapped for this role — showing all staff. Set the Designation / org chart to filter.'))
                : undefined}
              rules={[{ required: true, message: 'Reporting supervisor is required for activation' }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Pick reporting supervisor"
                options={supervisorOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="joining_date" label="Joining Date">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="work_location" label="Work Location">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="notice_period_days" label="Notice Period (days)">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="probation_applicable" label="Probation Applicable" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name="probation_end_date" label="Probation End Date">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col span={9}>
            <Form.Item name="confirmation_due_date" label="Confirmation Due Date">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Card size="small" title="Contractor / NAPS" style={{ marginBottom: 12 }}>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="contractor_name" label="Contractor Name">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="contractor_code" label="Contractor Code">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="labour_licence_no" label="Labour Licence No">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="naps_enrolled" label="NAPS Enrolled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="naps_enrolment_date" label="NAPS Enrolment Date">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Space>
        <Button type="primary" htmlType="submit" disabled={!dirty} loading={save.isPending}>
          Save
        </Button>
        {dirty && <span style={{ color: '#faad14' }}>Unsaved changes</span>}
      </Space>
    </Form>
  )
}
